/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  Inject
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ModalService, ModalRef } from '@wfm/core';
import { SortCriteria } from '../../types/sort-criteria';
import { VirtualGridColumn } from '../../types/virtual-grid-column';
import { FilterCriteria } from '../../filter/filter-criteria';
import { VirtualGridWorklist } from '../../types/virtual-grid-worklist';
import { VirtualGridTemplate } from '../../types/virtual-grid-template';
import { ChangeDetector } from '@wfm/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'virtual-grid-settings',
  templateUrl: './virtual-grid-settings.component.html',
  styleUrls: ['./virtual-grid-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualGridSettingsComponent extends ChangeDetector {
  @Input() activeListName: string;
  @Input() worklistColumnMetaData: Array<VirtualGridColumn>;
  @Input() managementWorklists: Array<VirtualGridWorklist>;

  @Input() launchTemplate: VirtualGridTemplate;
  @Input() templates: Array<VirtualGridTemplate>;
  @Input() isAdmin: boolean;
  @Input() selectedTemplate: VirtualGridTemplate;

  @Input() disableTemplateManagement?: boolean = false;

  @Input() columnFilterInclude: string[];
  @Input() filterCriteria: FilterCriteria[];
  @Input() sortCriteria: SortCriteria[];
  @Input() disabledSortColumns: string[];
  @Input() enableResetSortButton?: boolean = true;
  @Input() hideFilter = false;
  @Input() disabled = false;

  @Input() filterIcon: 'filter' | 'search' = 'filter';

  @Output() filterCriteriaChange = new EventEmitter<FilterCriteria[]>();
  @Output() sortCriteriaChange = new EventEmitter<SortCriteria[]>();
  @Output() resetSort = new EventEmitter();

  //Template Events
  @Output() selectedTemplateChange = new EventEmitter<VirtualGridTemplate>();
  @Output() vgsTemplateSelection = new EventEmitter<VirtualGridTemplate>();

  @Output() vgsapplyTemplateToWorklistsEvent = new EventEmitter<Array<number>>();
  @Output() vgsapplyDefaultTemplateToWorklistsEvent = new EventEmitter<Array<number>>();

  @Output() vgsApplyTemplateToWorklistForSession = new EventEmitter<Array<VirtualGridColumn>>();
  @Output() vgscreateNewTemplateEvent = new EventEmitter<Array<VirtualGridColumn>>();
  @Output() worklistColumnMetaDataChange = new EventEmitter<Array<VirtualGridColumn>>();

  @Output() vgsWorklistMetaSave = new EventEmitter<{ vgc: Array<VirtualGridColumn>; vgt: VirtualGridTemplate }>();

  /**
   * @Input()
   * exposed method to host application manually trigger the change detection.
   * When an attribute value is updated inside a complex type input, like an object or array,
   * the component do not automatically detect these changes, so the host application should manually
   * trigger the change decetion inside the component to update the template
   */
  @Input() get detectChanges() {
    return () => this.$detectChanges();
  }

  modalRef: ModalRef;

  templateMenuOpen = false;
  editorTab = 'templates';
  adminAccess = false;
  layoutMgmtHeader = '';

  @ViewChild('settingsTemplate') settingsTemplate;

  constructor(
    private modalService: ModalService,
    @Inject(DOCUMENT) document: any,
    cd: ChangeDetectorRef,
    private translate: TranslateService
  ) {
    super(document.defaultView, cd);
  }

  ngClickSettings(tab: 'templates' | 'management' = 'templates'): void {
    this.templateMenuOpen = false;
    this.editorTab = tab;

    this.modalRef = this.modalService.openModal(this.settingsTemplate, {
      headerContent: this.layoutMgmtHeader,
      modalClass: 'modal--medium',
      isDecisionModal: false,
      disableNoBtn: true,
      enableYesBtn: false
    });
  }

  // Local Menu Events
  selectTemplate(template: VirtualGridTemplate) {
    this.selectedTemplate = template;
    this.launchTemplate = template;
    this.templateMenuOpen = false;
    this.selectedTemplateChange.emit(template);
  }

  editTemplate(template: VirtualGridTemplate) {
    this.launchTemplate = template;
    this.selectedTemplate = template;
    this.adminAccess = false;
    this.ngClickSettings();
  }

  manageTemplates() {
    this.layoutMgmtHeader = this.translate.instant('vgl.manageMyTemplates');
    this.adminAccess = false;
    this.ngClickSettings('templates');
  }

  editCommonTemplate() {
    this.layoutMgmtHeader = this.translate.instant('vgl.manageCommonTemplates');
    this.adminAccess = true;
    this.ngClickSettings();
  }

  //pass through events from Grid Layout Component
  saveColumnMetaData(eventWorklistColumnMetaData: Array<VirtualGridColumn>) {
    this.worklistColumnMetaDataChange.emit(eventWorklistColumnMetaData);
  }

  templateselected(templateSelected: VirtualGridTemplate) {
    this.vgsTemplateSelection.emit(templateSelected);
  }

  applyTemplateToWorklistsEvent(worklistIdList: Array<number>) {
    this.vgsapplyTemplateToWorklistsEvent.emit(worklistIdList);
  }

  applyDefaultTemplateToWorklistsEvent(worklistIdList: Array<number>) {
    this.vgsapplyDefaultTemplateToWorklistsEvent.emit(worklistIdList);
  }

  createNewLayout(layoutEncoding: Array<VirtualGridColumn>) {
    this.vgscreateNewTemplateEvent.emit(layoutEncoding);
  }

  applyWithoutSaveAndClose(eventWorklistColumnMetaData: Array<VirtualGridColumn>) {
    this.vgsApplyTemplateToWorklistForSession.emit(eventWorklistColumnMetaData);
  }

  worklistMetaSaveFromLayout(gridAndTemplate) {
    this.vgsWorklistMetaSave.emit(gridAndTemplate);
  }

  closeModal() {
    this.modalRef.close();
  }

  toolTip = '';

  isTextTruncated(el): boolean {
    const curOverf = el.style.overflow;
    if (!curOverf || curOverf === 'visible') el.style.overflow = 'hidden';

    const isOverflowing = el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
    el.style.overflow = curOverf;
    if (isOverflowing) {
      this.toolTip = el.textContent;
    } else {
      this.toolTip = '';
    }
    return isOverflowing;
  }

  isFilterOpen = false;
}
